import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [membershipNumber, setMembershipNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [channel, setChannel] = useState('email'); // 'sms', 'whatsapp', or 'email'
  const [step, setStep] = useState('credentials'); // 'credentials' or 'otp'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const redirect = searchParams.get('redirect') || '/';
      navigate(redirect);
    }
  }, [user, navigate, searchParams]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Use email or phone based on selected channel
      const requestData = {
        membership_number: membershipNumber.trim(),
        channel: channel
      };
      
      if (channel === 'email') {
        requestData.email = email.toLowerCase();
      } else {
        requestData.email = email.toLowerCase() || 'placeholder@email.com'; // Backend still needs email field
        requestData.phone = phone.trim();
      }
      
      const { data } = await axios.post(`${API_BASE_URL}/api/auth/request-otp`, requestData);
      
      const channelName = channel === 'sms' ? 'SMS' : channel === 'whatsapp' ? 'WhatsApp' : 'Email';
      setSuccess(data.message || `OTP sent via ${channelName}. Please check your ${channel}.`);
      setStep('otp');
      setCountdown(60); // 60 second cooldown before resend
      
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to send OTP. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/auth/verify-otp`,
        {
          email: email.toLowerCase(),
          membership_number: membershipNumber.trim(),
          otp_code: otpCode
        },
        { withCredentials: true }
      );
      
      // Store tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      
      // Refresh auth context
      await checkAuth();
      
      // Navigate to redirect URL or home
      const redirect = searchParams.get('redirect') || '/';
      navigate(redirect);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpCode('');
    setError('');
    setSuccess('');
    
    // Actually resend the OTP by calling request-otp again
    await handleRequestOTP(new Event('submit'));
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setOtpCode('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">Welcome Back</h1>
          <p className="text-gray-600">
            {step === 'credentials' 
              ? 'Enter your membership number and choose how to receive OTP' 
              : 'Enter the OTP sent to you'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
            {success}
          </div>
        )}

        {step === 'credentials' ? (
          // Step 1: Membership Number, Choose Channel, then Email OR Phone
          <form onSubmit={handleRequestOTP} className="space-y-4">
            <div>
              <Label htmlFor="membershipNumber">Membership Registration Number</Label>
              <Input
                id="membershipNumber"
                type="text"
                value={membershipNumber}
                onChange={(e) => setMembershipNumber(e.target.value)}
                placeholder="CSED-ADMIN-001"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your CSED membership number
              </p>
            </div>

            {/* Channel Selection - SMS/WhatsApp temporarily hidden until Twilio verification complete */}
            <div>
              <Label>Receive OTP via Email</Label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center cursor-pointer p-3 border-2 rounded-lg bg-blue-50"
                  style={{ borderColor: '#E87722' }}>
                  <input
                    type="radio"
                    name="channel"
                    value="email"
                    checked={true}
                    readOnly
                    className="mr-3"
                  />
                  <span className="text-sm">📧 <strong>Email OTP</strong> - Secure code sent to your email inbox</span>
                </label>
                
                {/* SMS and WhatsApp options - Temporarily hidden 
                <label className="flex items-center cursor-pointer p-3 border-2 rounded-lg hover:bg-blue-50 transition-colors"
                  style={{ borderColor: channel === 'sms' ? '#E87722' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="channel"
                    value="sms"
                    checked={channel === 'sms'}
                    onChange={(e) => setChannel(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-sm">📱 <strong>SMS</strong> - Text message to your mobile</span>
                </label>
                <label className="flex items-center cursor-pointer p-3 border-2 rounded-lg hover:bg-blue-50 transition-colors"
                  style={{ borderColor: channel === 'whatsapp' ? '#E87722' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="channel"
                    value="whatsapp"
                    checked={channel === 'whatsapp'}
                    onChange={(e) => setChannel(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-sm">💬 <strong>WhatsApp</strong> - Message to your WhatsApp</span>
                </label>
                */}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 SMS and WhatsApp OTP will be enabled soon
              </p>
            </div>
            
            {/* Conditional Field: Email OR Phone based on channel */}
            {channel === 'email' ? (
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your registered email address
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="phone">Mobile Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+919876543210"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your registered mobile number (with country code)
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#E87722] hover:bg-[#d16b1a]"
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>

            <div className="text-center text-sm text-gray-600 space-y-2">
              <p>Don't have an account?{' '}
                <Link to="/register" className="text-[#E87722] hover:underline">
                  Register here
                </Link>
              </p>
              <p>
                <Link to="/" className="text-gray-500 hover:underline">
                  ← Back to Home
                </Link>
              </p>
            </div>
          </form>
        ) : (
          // Step 2: Verify OTP
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div>
              <Label htmlFor="contact">
                {channel === 'email' ? 'Email Address' : 'Mobile Number'}
              </Label>
              <Input
                id="contact"
                type="text"
                value={channel === 'email' ? email : phone}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                {channel === 'sms' && '📱 OTP sent via SMS'}
                {channel === 'whatsapp' && '💬 OTP sent via WhatsApp'}
                {channel === 'email' && '📧 OTP sent to your email'}
              </p>
            </div>

            <div>
              <Label htmlFor="otp">OTP Code</Label>
              <Input
                id="otp"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                required
                disabled={loading}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                {channel === 'sms' && 'Check your SMS messages for the 6-digit code'}
                {channel === 'whatsapp' && 'Check your WhatsApp messages for the 6-digit code'}
                {channel === 'email' && 'Check your email inbox for the 6-digit code'}
              </p>
            </div>

            <div className="text-sm text-gray-600">
              <p className="mb-2">
                Didn't receive the code?{' '}
                {countdown > 0 ? (
                  <span className="text-gray-400">
                    Resend in {countdown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="text-[#E87722] hover:underline"
                  >
                    Resend OTP
                  </button>
                )}
              </p>
              <button
                type="button"
                onClick={handleBackToCredentials}
                className="text-gray-500 hover:underline text-xs"
              >
                ← Change {channel === 'email' ? 'email' : 'mobile number'} or delivery method
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#E87722] hover:bg-[#d16b1a]"
              disabled={loading || otpCode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </Button>

            <div className="text-center text-sm text-gray-600">
              <p>
                <Link to="/" className="text-gray-500 hover:underline">
                  ← Back to Home
                </Link>
              </p>
            </div>
          </form>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
          <p className="text-xs text-blue-800">
            🔒 <strong>Secure Login:</strong> Enter your membership number and email address, 
            then receive a one-time password (OTP) via email. This OTP is valid for 10 minutes.
          </p>
        </div>
      </Card>
    </div>
  );
}
